import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { AttendanceZone, AttendanceZonesResponse } from './zone.interface';

interface CompaniesResponse {
  data?: CompaniesRecord[];
  items?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

@Component({
  selector: 'app-zone',
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule],
  templateUrl: './zone.component.html',
  styleUrl: './zone.component.scss',
})
export class ZoneComponent implements OnInit {
  dataSource = new MatTableDataSource<AttendanceZone>([]);
  companies: CompaniesRecord[] = [];
  isLoading = true;
  actions: TableAction<AttendanceZone>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<AttendanceZone>[] = [];
  searchValue = '';
  activeFilters: Record<string, string | number | boolean | null> = {};
  currentPage = 1;
  pageSize = 15;
  totalItems = 0;
  private loadRequestSequence = 0;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureTranslations();
      this.tableFilters = this.buildTableFilters();
      this.dataSource.data = this.dataSource.data.map((zone) => this.normalizeZone(zone));
    });
    void this.initializeData();
  }

  async initializeData(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadCompanies();
      this.tableFilters = this.buildTableFilters();
      await this.loadData();
    } finally {
      this.isLoading = false;
    }
  }

  async loadData(): Promise<void> {
    const requestId = ++this.loadRequestSequence;
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceZonesResponse>('attendance/zones', {
          params: {
            page: this.currentPage,
            perPage: this.pageSize,
            ...(this.searchValue ? { search: this.searchValue } : {}),
            ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
            ...(this.getActiveStatus() !== '' ? { isActive: this.getActiveStatus() } : {}),
          },
        })
      );

      if (requestId !== this.loadRequestSequence) return;

      this.totalItems = response?.pagination?.total || 0;
      this.dataSource.data = (response?.data || []).map((zone) => this.normalizeZone(zone));
      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      if (requestId !== this.loadRequestSequence) return;
      console.error('Error loading attendance zones', error);
      this.dataSource.data = [];
      this.totalItems = 0;
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('zonesCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      if (requestId === this.loadRequestSequence) this.isLoading = false;
    }
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/zones/new']);
  }

  async handleView(row: AttendanceZone): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/zones', row.zoneId]);
  }

  async handleEdit(row: AttendanceZone): Promise<void> {
    if (!this.hasAccess('update')) {
      return;
    }
    await this.router.navigate(['/attendance/zones', row.zoneId, 'edit']);
  }

  handleDelete(row: AttendanceZone): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('zonesCatalog.dialog.deleteTitle'),
        message: this.t('zonesCatalog.dialog.deleteMessage', { zone: row.name }),
        confirmLabel: this.t('zonesCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance/zones/${row.zoneId}`));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('zonesCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting attendance zone', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('zonesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: AttendanceZone[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(rows.map((row) => firstValueFrom(this.apiService.delete(`attendance/zones/${row.zoneId}`))));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting attendance zones', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('zonesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onSearchChange(search: string): void {
    this.searchValue = search.trim();
    this.currentPage = 1;
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => {
      void this.loadData();
    }, 300);
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null>): void {
    this.activeFilters = filters;
    this.currentPage = 1;
    void this.loadData();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    void this.loadData();
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', {
          params: { isActive: 'true' },
        })
      );
      this.companies = this.extractCompanies(response)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
    } catch (error) {
      console.error('Error loading companies for attendance zones', error);
      this.companies = [];
    }
  }

  private extractCompanies(response: CompaniesRecord[] | CompaniesResponse): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.companiesRecords || [];
  }

  private normalizeZone(zone: Partial<AttendanceZone>): AttendanceZone {
    const isActive = zone.isActive !== false;
    const lat = Number(zone.lat || 0);
    const lng = Number(zone.lng || 0);
    const radius = Number(zone.radiusMeters || 0);
    return {
      zoneId: zone.zoneId || crypto.randomUUID(),
      center: zone.center || '',
      companiesRecordId: zone.companiesRecordId || '',
      name: zone.name || '',
      icon: zone.icon || 'place',
      type: 'circle',
      lat,
      lng,
      radiusMeters: radius,
      geohash: zone.geohash || '',
      isActive,
      companyName: this.companies.find((company) => company.companiesRecordId === zone.companiesRecordId)?.name || '-',
      typeLabel: this.t('zonesCatalog.types.circle'),
      radiusLabel: this.t('zonesCatalog.labels.radiusMeters', { value: radius }),
      coordinatesLabel: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private buildTableFilters(): TableFilterDefinition<AttendanceZone>[] {
    return [
      {
        id: 'company',
        label: this.t('zonesCatalog.filters.company'),
        placeholder: this.t('zonesCatalog.filters.companyPlaceholder'),
        icon: 'business',
        searchable: true,
        value: this.activeFilters['company'] ?? null,
        options: this.companies.map((company) => ({
          value: company.companiesRecordId,
          label: company.name,
        })),
      },
      {
        id: 'status',
        label: this.t('zonesCatalog.filters.status'),
        placeholder: this.t('zonesCatalog.filters.statusPlaceholder'),
        icon: 'verified',
        searchable: false,
        value: this.activeFilters['status'] ?? null,
        options: [
          { value: 'true', label: this.t('common.status.active') },
          { value: 'false', label: this.t('common.status.inactive') },
        ],
      },
    ];
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('zonesCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'zones', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('zonesCatalog.actions.view'),
        colorClass: 'info',
        auth: { resource: 'zones', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('zonesCatalog.actions.edit'),
        colorClass: 'accent',
        auth: { resource: 'zones', action: 'update' },
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('zonesCatalog.actions.delete'),
        colorClass: 'danger',
        auth: { resource: 'zones', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'name', label: this.t('zonesCatalog.fields.name'), type: 'text', visible: true },
      { def: 'companyName', label: this.t('orgUnitsCatalog.fields.company'), type: 'text', visible: true },
      { def: 'typeLabel', label: this.t('zonesCatalog.fields.type'), type: 'text', visible: true },
      { def: 'coordinatesLabel', label: this.t('zonesCatalog.fields.coordinates'), type: 'text', visible: true },
      { def: 'radiusLabel', label: this.t('zonesCatalog.fields.radiusMeters'), type: 'text', visible: true },
      { def: 'activeLabel', label: this.t('zonesCatalog.fields.status'), type: 'status', visible: true },
    ];

    this.tableFilters = this.buildTableFilters();
  }

  private getActiveCompanyId(): string {
    return (this.activeFilters['company'] as string) || '';
  }

  private getActiveStatus(): string {
    const value = this.activeFilters['status'];
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return String(value);
  }

  private hasAccess(action: string): boolean {
    return this.authorizationService.hasAccess({ resource: 'zones', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2200,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
